import { useMemo } from 'react'

import { HStack, Text, VStack, Tooltip } from 'govwell-ui'
import { AIChatFragmentForSessionTable, AIChatSessionFragmentForSessionTable } from 'types/graphql'

import Tag from 'src/components/Tag'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { formatUserName } from 'src/utils'
import { DateFormats, formatDate, getRelativeDateTimeString } from 'src/utils/date'

type Props = {
  session: AIChatSessionFragmentForSessionTable
  chat: AIChatFragmentForSessionTable | null | undefined
}

const AIChatSessionItem = ({ session, chat }: Props) => {
  const user = useMemo(() => session.user ?? null, [session.user])
  const websiteLabel = useMemo(
    () => session.organization?.displayName ?? 'Organization',
    [session.organization]
  )
  const formattedUserName = useMemo(() => (user ? formatUserName(user) : 'Anonymous'), [user])
  return (
    <HStack padding="8px" justify="center">
      <HStack gap={3}>
        <UserAvatar user={user} sizePx={24} />
        <VStack align="start" justify="center" gap={2}>
          <Text as="span" className="font-semibold">
            {formattedUserName}
          </Text>
          <Text as="span" className="text-gray-600">
            {chat?.question}
          </Text>
        </VStack>
      </HStack>
      <VStack gap={2} justify="center" className="!items-end">
        <Tooltip content={`Asked ${formatDate(session.startedAt, DateFormats.MonthNameDateTime)}`}>
          <Text as="span" className="whitespace-nowrap text-gray-600">
            {getRelativeDateTimeString(session.startedAt)}
          </Text>
        </Tooltip>
        <HStack gap={2} justify="end" className="flex-1">
          {session.anyChatHasCustomAnswer && (
            <Tooltip
              asChild={false}
              content="A Custom Answer was created from a chat in this session."
            >
              <Tag color="yellow">Custom Answer</Tag>
            </Tooltip>
          )}
          {session.source === 'GovWell' ? (
            <Tooltip content="Asked in the GovWell portal" asChild={false}>
              <Tag color="orange">GovWell</Tag>
            </Tooltip>
          ) : (
            <Tooltip content={`Asked on ${session.sourceUrl}`} asChild={false}>
              <Tag color="blue">{websiteLabel} website</Tag>
            </Tooltip>
          )}
        </HStack>
      </VStack>
    </HStack>
  )
}

export default React.memo(AIChatSessionItem)
